import { z } from 'zod';
import { LEAGUE_ROLES, POOL_MODES, SEASON_STATUSES, SLATE_STATUSES } from '../constants';
import type { LeagueRole, PoolMode, RevealPolicy, SeasonStatus, SlateStatus } from '../constants';

/**
 * Password policy. Length beats composition rules — a minimum plus a blocklist of the
 * obvious choices stops far more real-world compromises than forcing a symbol into
 * `Password1!`.
 *
 * Eight characters, not the usual longer minimum, because the threat model is honest about
 * what this is: a private pick'em for a dozen friends, holding no money and no personal
 * data beyond a display name. The realistic risk here is somebody locked out of their own
 * league, not a targeted attack — and a minimum long enough to push people toward reusing
 * a password they already have elsewhere trades a real problem for a theoretical one.
 */
export const password = z
  .string()
  .min(8, 'Use at least 8 characters')
  .max(200, 'That password is too long');

export const email = z
  .string()
  .trim()
  .toLowerCase()
  .email('Enter a valid email address')
  .max(254);

export const displayName = z
  .string()
  .trim()
  .min(2, 'Display name must be at least 2 characters')
  .max(32, 'Display name must be 32 characters or fewer')
  .regex(/^[\p{L}\p{N}][\p{L}\p{N} '._-]*$/u, 'Letters, numbers, spaces and . _ - only');

/** Invite codes are typed by humans, so accept any case and ignore stray spaces. */
export const inviteCode = z
  .string()
  .trim()
  .toUpperCase()
  .min(4)
  .max(24)
  .regex(/^[A-Z0-9-]+$/, 'Invite codes are letters, numbers and dashes');

export const registerBody = z.object({
  email,
  password,
  displayName,
  inviteCode,
});
export type RegisterBody = z.infer<typeof registerBody>;

export const loginBody = z.object({
  email,
  password: z.string().min(1, 'Enter your password').max(200),
});
export type LoginBody = z.infer<typeof loginBody>;

export const forgotPasswordBody = z.object({ email });
export type ForgotPasswordBody = z.infer<typeof forgotPasswordBody>;

export const resetPasswordBody = z.object({
  token: z.string().min(16).max(200),
  password,
});
export type ResetPasswordBody = z.infer<typeof resetPasswordBody>;

export const updateProfileBody = z
  .object({
    displayName: displayName.optional(),
    currentPassword: z.string().min(1).max(200).optional(),
    newPassword: password.optional(),
  })
  .refine((v) => v.displayName !== undefined || v.newPassword !== undefined, {
    message: 'Nothing to update',
  })
  // Changing a password always requires proving you know the current one, even though the
  // session is already authenticated — it stops a borrowed phone becoming a stolen account.
  .refine((v) => v.newPassword === undefined || v.currentPassword !== undefined, {
    message: 'Enter your current password to change it',
    path: ['currentPassword'],
  });
export type UpdateProfileBody = z.infer<typeof updateProfileBody>;

export const joinLeagueBody = z.object({ inviteCode });
export type JoinLeagueBody = z.infer<typeof joinLeagueBody>;

// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------

export interface UserDto {
  id: string;
  email: string;
  displayName: string;
  createdAt: string;
  /**
   * Where to fetch this user's picture, or null if they have not set one.
   *
   * Relative by default (`/api/users/:id/avatar?v=3`), which is correct whenever the web
   * app and the API share an origin. The `?v=` is what makes the long immutable cache on
   * that endpoint safe: a new picture is a new URL rather than a stale one to invalidate.
   */
  avatarUrl: string | null;
}

/**
 * Uploading a profile picture.
 *
 * The image arrives already resized and re-encoded by the browser rather than as the
 * original camera file. A phone photo is several megabytes of data nobody needs to store
 * to draw a 40-pixel circle, and doing the work client-side means the server never has to
 * decode untrusted image data to shrink it — the size cap alone does that job.
 */
export const uploadAvatarBody = z.object({
  mimeType: z.enum(['image/webp', 'image/jpeg', 'image/png']),
  /** base64, no `data:` prefix. Capped so the decoded image stays under 256KB. */
  data: z.string().min(32).max(360_000),
});
export type UploadAvatarBody = z.infer<typeof uploadAvatarBody>;

export interface MembershipDto {
  leagueId: string;
  leagueName: string;
  leagueSlug: string;
  role: LeagueRole;
  joinedAt: string;
}

export interface SeasonConfigDto {
  id: string;
  leagueId: string;
  year: number;
  name: string;
  status: SeasonStatus;
  gamesPerWeek: number;
  poolMode: PoolMode;
  mixedNflCount: number | null;
  mixedCfbCount: number | null;
  winPoints: number;
  lossPoints: number;
  pushPoints: number;
  revealPolicy: RevealPolicy;
  displayTimezone: string;
}

export interface WeekSummaryDto {
  id: string;
  weekNumber: number;
  label: string;
  slateStatus: SlateStatus;
  windowStart: string;
  windowEnd: string;
  publishedAt: string | null;
  picksLockedOverride: boolean | null;
  gameCount: number;
  firstKickoffAt: string | null;
  lastKickoffAt: string | null;
}

/** `GET /auth/me` — everything the shell needs to route and render nav on first paint. */
export interface SessionDto {
  user: UserDto;
  memberships: MembershipDto[];
  /** The league the app opens into. Null for a brand-new user with no league yet. */
  activeLeagueId: string | null;
  activeRole: LeagueRole | null;
  /**
   * Whether this member may open the roster page.
   *
   * Sent so the client knows whether to offer the tab at all. It is not what enforces
   * anything — the server checks the same grant on every read — but a link to a page that
   * would refuse you is a worse experience than no link.
   */
  canViewRoster: boolean;
  activeSeason: SeasonConfigDto | null;
  /** The week the app opens into: the newest published week, else the newest week. */
  currentWeek: WeekSummaryDto | null;
}

export const roleEnum = z.enum(LEAGUE_ROLES);
export const poolModeEnum = z.enum(POOL_MODES);
export const seasonStatusEnum = z.enum(SEASON_STATUSES);
export const slateStatusEnum = z.enum(SLATE_STATUSES);

// ---------------------------------------------------------------------------
// Push notifications
// ---------------------------------------------------------------------------

/**
 * A browser's push subscription, forwarded verbatim from `PushManager.subscribe()`.
 *
 * The endpoint is the address of one device's mailbox at its push service, and the two
 * keys are what let that service hand an encrypted payload to the right browser. All three
 * come from the browser and none of them mean anything to us beyond being passed back.
 */
export const pushSubscribeBody = z.object({
  endpoint: z.string().url().max(2000),
  keys: z.object({
    p256dh: z.string().min(16).max(200),
    auth: z.string().min(8).max(100),
  }),
});
export type PushSubscribeBody = z.infer<typeof pushSubscribeBody>;

export const pushUnsubscribeBody = z.object({ endpoint: z.string().url().max(2000) });
export type PushUnsubscribeBody = z.infer<typeof pushUnsubscribeBody>;

/**
 * The device list is fetched with a POST, unusually for a read.
 *
 * A push endpoint is a delivery capability, not an identifier — anyone holding one can
 * send to that device. Putting it in a query string would write it into browser history,
 * proxy logs and referrers, so it travels in a body instead. It is optional: without it
 * the list still comes back, just with nothing marked as the device you are holding.
 */
export const pushDevicesBody = z.object({
  endpoint: z.string().url().max(2000).optional(),
});
export type PushDevicesBody = z.infer<typeof pushDevicesBody>;

export interface PushDeviceDto {
  id: string;
  /** Best-effort from the user agent: `iPhone · Safari`. Never precise, only recognisable. */
  label: string;
  /** True for the device making the request, which is the one you cannot re-add remotely. */
  isThisDevice: boolean;
  /** Null until something has actually been delivered to it. */
  lastSeenAt: string | null;
  addedAt: string;
}

export interface PushStatusDto {
  /** False when the server has no VAPID pair, so subscribing would be pointless. */
  configured: boolean;
  /** The VAPID public key the browser needs to subscribe. Null when not configured. */
  publicKey: string | null;
  /** How many devices this user currently has subscribed. */
  deviceCount: number;
  /** Hours before the first kickoff that the two reminders fire. */
  reminderHours: number;
  finalCallHours: number;
}

/** What the service worker receives and turns into a notification. */
export interface PushPayload {
  title: string;
  body: string;
  /** Collapses repeats: a second notification with the same tag replaces the first. */
  tag: string;
  /** Path to open when the notification is tapped. */
  url: string;
  /**
   * Absolute URL of a large image to show inside the notification.
   *
   * Honoured by Android Chrome and ignored by most everything else, so it decorates a
   * notification rather than carrying any of its meaning — the title and body have to
   * stand on their own. Must be absolute and served without credentials: the fetch is made
   * by the push handler, which has no session cookie.
   */
  image?: string;
}
